/**
 * The Yanyu region (docs/archive/OPEN_WORLD.md, option B), rearranged for the hub city (docs/archive/HUB.md, option ก,
 * นครกลางลำน้ำ): a long river valley from the coast to the southern gorge inside a natural outline,
 * its bounding square 3,456 m, as a sketch of vector shapes.
 * Ported from `scripts/layouts/open-world/region-b.mjs` (the plan images' sketch), shape for shape. Data
 * only: `region/sketch.ts` compiles it and `region/generate.ts` turns it into chunks.
 *
 * Metres in the region: x east, y south; heights in metres above the sea.
 */
import type { RegionSketch } from '../../region/sketch';

export const YANYU_REGION: RegionSketch = {
  id: 'yanyu',
  size: 3456,
  seed: 9,
  // The village's tile (0, 0) in the region (its layout is 160 × 160).
  core: { x: 1408, y: 384, layout: 'yanyu' },
  // The north coast: the sea lies north of it. Two headlands hold the bay of Yanyu between them.
  coast: [
    [0, 360], [220, 330], [480, 350], [700, 320], [860, 280], [960, 190], [1040, 110], [1130, 90],
    [1210, 130], [1260, 230], [1320, 330], [1380, 390], [1408, 400], [1568, 400], [1600, 420],
    [1700, 470], [1800, 470], [1880, 440], [1960, 400], [2060, 330], [2150, 220], [2230, 130],
    [2320, 100], [2400, 140], [2460, 230], [2560, 300], [2760, 330], [3072, 300], [3456, 300],
  ],
  islands: [
    { x: 1660, y: 300, r: 26, h: 9, name: 'หินกลางอ่าว' },
    { x: 1300, y: 200, r: 70, h: 22, name: 'เกาะสนสามต้น' },
    { x: 2010, y: 170, r: 120, h: 34, name: 'เกาะอ่าวโจรสลัด' },
    { x: 1760, y: 120, r: 46, h: 16, name: 'เกาะประภาคาร' },
    { x: 2600, y: 150, r: 60, h: 18, name: 'เกาะเต่า' },
    { x: 520, y: 210, r: 80, h: 26, name: 'เกาะเมฆขาว' },
  ],
  // Water-skim legs across the bay: stepping stones every ~50 m along each.
  stoneLegs: [
    [[1560, 400], [1660, 300]],
    [[1660, 300], [1760, 120]],
    [[1660, 300], [1300, 200]],
    [[1760, 120], [2010, 170]],
  ],
  peaks: [
    // Wudang massif
    { x: 520, y: 1000, h: 380, r: 330 }, // the golden summit (ยอดทอง)
    { x: 330, y: 760, h: 300, r: 260 },
    { x: 720, y: 1290, h: 250, r: 260 },
    { x: 250, y: 1320, h: 290, r: 300 },
    { x: 160, y: 520, h: 230, r: 260 },
    { x: 880, y: 700, h: 170, r: 240 },
    // the coastal range from Yanyu's western cliffs to the west headland
    { x: 1180, y: 330, h: 70, r: 170 },
    { x: 1080, y: 160, h: 60, r: 120 },
    { x: 1300, y: 480, h: 52, r: 120 },
    // pine hills east of Yanyu and the bamboo hills
    { x: 1720, y: 640, h: 48, r: 200 },
    { x: 1960, y: 820, h: 60, r: 220 },
    { x: 2200, y: 960, h: 72, r: 230 },
    { x: 2120, y: 640, h: 50, r: 170 },
    // east headland
    { x: 2330, y: 200, h: 52, r: 130 },
    // eastern ranges round the pleasure valley
    { x: 2900, y: 900, h: 190, r: 330 },
    { x: 2950, y: 1500, h: 170, r: 320 },
    { x: 2880, y: 2050, h: 180, r: 320 },
    { x: 2660, y: 1180, h: 110, r: 180 },
    // low hills between the village and the town
    { x: 1220, y: 880, h: 40, r: 200 },
    { x: 1760, y: 1180, h: 34, r: 220 },
    // bandit hills south-west of the city (the nearest is levelled under the city's plain)
    { x: 900, y: 1960, h: 95, r: 260 },
    { x: 640, y: 2240, h: 130, r: 320 },
    { x: 300, y: 1900, h: 200, r: 330 },
    // south of the town
    { x: 2050, y: 2250, h: 70, r: 260 },
    { x: 1150, y: 2450, h: 80, r: 260 },
    // the red cliff (south-east)
    { x: 2700, y: 2620, h: 210, r: 220 },
  ],
  // The southern range, 440 m further south than before the city (docs/archive/HUB.md §5): the valley runs
  // on into the gorge and the south pass on the range's line.
  southRidge: {
    pts: [[0, 3300], [500, 3290], [900, 3350], [1300, 3390], [1700, 3370], [2100, 3320], [2500, 3360], [3456, 3320]],
    h: 250,
    reach: 340,
    edgeFrom: 3150,
  },
  // Each pass sits on the outline's edge, where it cuts through the rim.
  passes: [
    { id: 'south', x: 1475, y: 3395, r: 110, name: 'ด่านใต้', to: 'pine-ink' },
    { id: 'redcliff', x: 2560, y: 2958, r: 100, name: 'ด่านผาแดง', to: 'cinnabar' },
    { id: 'west', x: 444, y: 2300, r: 120, name: 'ด่านตะวันตก', to: 'bell-stone' },
    { id: 'north', x: 356, y: 620, r: 110, name: 'ช่องเขาเหนือ', to: 'lantern-road' },
  ],
  // Option ก's long valley (docs/archive/HUB.md §2, `OUTLINES.A` in scripts/layouts/hub-layout.mjs), with a bulge
  // south-east that keeps the red cliff and its pass inside (2,880 / 2,640 / 2,280 for 2,300, 2,800).
  outline: [
    [300, 60], [1100, 40], [1300, 200], [1700, 280], [2300, 120], [2700, 300], [2780, 900], [2600, 1300],
    [2780, 1800], [2700, 2400], [2880, 2660], [2640, 2990], [2280, 2850], [1900, 3100], [1650, 3400],
    [1300, 3400], [1150, 3000], [700, 2700], [380, 2200], [420, 1600], [260, 1100], [380, 500],
  ],
  flats: [
    // The city's plain (walls 880 × 740 m and the suburbs), flat at 22 m.
    { x: 1460, y: 1650, r: 700, h: 22, name: 'hub', noise: 0 },
    // North of the city's barbican, so the city's plain stays at 22 m.
    { x: 1480, y: 990, r: 180, h: 17, name: 'midway fields' },
    { x: 1500, y: 760, r: 200, h: 12, name: 'south of the village' },
    // 50 m east of where it was and 40 m smaller, so it stops short of the city's east wall.
    { x: 2400, y: 1520, r: 380, h: 34, name: 'pleasure valley' },
    { x: 720, y: 1020, r: 110, h: 185, name: 'mist-peak compound terraces', noise: 0 },
    { x: 520, y: 1000, r: 36, h: 380, name: 'golden summit', noise: 0 },
    // The stone forest, shrunk north of the city's wall (y ≤ 1,240).
    { x: 1080, y: 1020, r: 190, h: 42, name: 'stone forest floor' },
    { x: 960, y: 820, r: 120, h: 40, name: 'mist-peak foot village', noise: 0 },
    // The pagoda's hill outside the west gate, 10 m over the plain.
    { x: 880, y: 1560, r: 80, h: 32, name: 'pagoda hill', noise: 0 },
    // The bandit camp's shelf in the hills.
    { x: 700, y: 2160, r: 100, h: 34, name: 'bandit camp', noise: 1 },
  ],
  rivers: [
    {
      name: 'ลำธารเมฆา',
      pts: [[640, 820], [760, 760], [900, 700], [1020, 620], [1120, 560], [1220, 500], [1290, 400], [1320, 330]],
      bed: [150, 40, 30, 22, 14, 8, 2, -1],
      w: [10, 14, 16, 18, 20, 22, 26, 30],
    },
    {
      name: 'แม่น้ำสราญ',
      pts: [
        [2250, 2850], [2350, 2660], [2280, 2460], [2370, 2260], [2320, 2060], [2400, 1860], [2380, 1650],
        [2370, 1380], [2300, 1250], [2180, 1150], [2120, 1040], [2020, 960], [2040, 840], [1960, 760],
        [1920, 640], [1860, 540], [1840, 450],
      ],
      bed: [140, 90, 66, 50, 40, 33, 30, 29, 26, 21, 18, 15, 12, 9, 5, 2, -1],
      w: [10, 12, 14, 16, 18, 20, 20, 24, 26, 28, 28, 30, 32, 34, 36, 40, 46],
    },
    {
      name: 'ลำน้ำศิลา',
      // Through the city's east third in a straight 36 m reach (water gates at (1666, 2016) and
      // (1854, 1284)), 1–1.5 m under the plain; west of the ruined temple upstream.
      pts: [[1650, 2640], [1610, 2420], [1720, 2220], [1660, 2040], [1760, 1650], [1860, 1260], [1930, 1150], [1990, 1060], [2020, 960]],
      bed: [34, 27, 21.6, 21, 20.7, 20.4, 17, 15.3, 15],
      w: [8, 10, 14, 30, 36, 36, 22, 18, 20],
    },
  ],
  falls: [
    { x: 690, y: 790, drop: 105, name: 'น้ำตกเมฆา' },
    { x: 2330, y: 2160, drop: 30, name: 'น้ำตกสราญ' },
    { x: 2620, y: 1440, drop: 60, name: 'น้ำตกหน้าผาตะวันออก' },
  ],
  lakes: [
    { x: 2400, y: 1520, rx: 230, ry: 150, h: 30, name: 'ทะเลสาบสราญ' },
    { x: 610, y: 890, rx: 40, ry: 30, h: 148, name: 'บึงเมฆ' },
    // Inside the walls: the reed marsh is the city's park lake, and the lotus lake of the garden quarter.
    { x: 1160, y: 1850, rx: 80, ry: 40, h: 21.4, name: 'บึงกก' },
    { x: 1215, y: 1360, rx: 70, ry: 48, h: 21.4, name: 'บึงบัว' },
  ],
  // Ground cover zones: [cover, x, y, r]. Later wins.
  cover: [
    ['bamboo', 2030, 860, 330],
    ['bamboo', 2240, 1060, 200],
    ['bamboo', 1060, 1020, 140],
    ['pine', 1700, 620, 210],
    ['farm', 1470, 1080, 250],
    ['farm', 1470, 800, 150],
    ['farm', 1700, 2300, 160],
    ['farm', 1180, 2250, 140],
    ['tea', 880, 940, 170],
    ['flower', 2210, 1700, 170],
    ['flower', 2560, 1700, 140],
    ['blossom', 2180, 1400, 110],
    ['blossom', 2620, 1350, 90],
    ['forest', 1100, 2150, 260],
    ['forest', 1900, 2000, 250],
    ['forest', 1250, 620, 140],
    ['redrock', 2700, 2620, 230],
  ],
  roads: [
    // To the north barbican's outer gate (its passage on the east, x 1470–1482).
    { id: 'south', name: 'ถนนใต้ หยานหยู–ลานศิลา', main: true, pts: [[1488, 538], [1490, 700], [1478, 880], [1500, 1050], [1476, 1180], [1476, 1234]] },
    // The city's streets (walked by the road graph; the city's paving is the town ground).
    // ถนนหลวง: through the barbican and the north gate, round the stone, to the south gate.
    {
      id: 'luang',
      name: 'ถนนหลวง',
      main: true,
      pts: [[1476, 1234], [1476, 1262], [1450, 1262], [1450, 1610], [1478, 1622], [1478, 1678], [1450, 1690], [1450, 2016]],
    },
    // From the stone out of the west gate to the pagoda.
    { id: 'pagoda', name: 'ถนนเจดีย์', main: true, pts: [[1478, 1622], [1430, 1622], [1424, 1650], [1012, 1650], [930, 1650], [880, 1610]] },
    { id: 'boss', name: 'ทางลานจอมมาร', pts: [[1476, 1180], [1400, 1170], [1335, 1152]] },
    { id: 'westpass', name: 'ถนนช่องเขาตะวันตก', main: true, pts: [[1414, 442], [1340, 470], [1250, 520], [1150, 600], [1060, 690], [960, 790]] },
    { id: 'steps', name: 'บันไดหมื่นขั้น', pts: [[960, 790], [900, 880], [830, 950], [760, 1000]] },
    { id: 'summit', name: 'ทางยอดทอง', pts: [[700, 1030], [620, 1030], [560, 1010]] },
    { id: 'stoneforest', name: 'ถนนป่าเสาหิน', main: true, pts: [[930, 1650], [930, 1500], [960, 1300], [1000, 1150], [960, 1000], [960, 790]] },
    // From the stone over the drum tower's passage and the rainbow bridge, out of the east gate.
    {
      id: 'east',
      name: 'ถนนตะวันออก',
      main: true,
      pts: [[1478, 1678], [1490, 1650], [1590, 1650], [1700, 1650], [1900, 1650], [2000, 1630], [2100, 1590], [2160, 1560]],
    },
    // From the south gate through the south barbican (its passage on the east) to the pass.
    {
      id: 'southpass',
      name: 'ถนนใต้ ลานศิลา–ด่านใต้',
      main: true,
      pts: [[1450, 2016], [1450, 2037], [1476, 2037], [1476, 2070], [1440, 2200], [1470, 2450], [1475, 2800], [1475, 3385]],
    },
    { id: 'redcliff', name: 'ถนนผาแดง', main: true, pts: [[2200, 1650], [2300, 1900], [2450, 2300], [2560, 2945]] },
    { id: 'southwest', name: 'ถนนตะวันตกเฉียงใต้', main: true, pts: [[1020, 1860], [880, 1940], [790, 2050], [640, 2090], [540, 2180], [450, 2300]] },
    { id: 'northpass', name: 'ทางช่องเขาเหนือ', pts: [[700, 1000], [520, 870], [400, 700], [370, 625]] },
    { id: 'eastcoast', name: 'ทางเลียบผาตะวันออก', pts: [[1560, 430], [1700, 500], [1850, 480], [1990, 420], [2120, 300], [2280, 200]] },
    { id: 'bamboo', name: 'ทางทะเลไผ่', pts: [[1500, 1050], [1700, 1000], [1900, 960], [2100, 1100], [2250, 1250], [2250, 1440]] },
    { id: 'headland', name: 'ทางผาหัวมังกร', pts: [[1250, 520], [1200, 380], [1120, 220], [1080, 140]] },
  ],
  // Paved or trodden ground of towns, compounds, camps and ruins (the sketch's `town` calls).
  grounds: [
    { x: 1460, y: 1650, rw: 440, rh: 370, cover: 'town' }, // the city inside its walls
    { x: 1450, y: 1185, rw: 150, rh: 55, cover: 'town' }, // the fair outside the north gate
    { x: 1450, y: 2100, rw: 110, rh: 80, cover: 'town' }, // the south suburb
    { x: 880, y: 1570, rw: 60, rh: 60, cover: 'town' }, // the pagoda's temple on its hill
    { x: 1320, y: 1150, rw: 32, rh: 32, round: true, cover: 'ruin' }, // ลานจอมมาร (the world boss's ground)
    { x: 720, y: 1020, rw: 70, rh: 55, cover: 'town' }, // Wudang's halls
    { x: 960, y: 820, rw: 60, rh: 45, cover: 'town' }, // the foot village
    { x: 2150, y: 1560, rw: 70, rh: 55, cover: 'town' }, // the lakeside town
    { x: 1510, y: 932, rw: 24, rh: 22, cover: 'town' }, // the waystation
    { x: 1505, y: 3290, rw: 40, rh: 26, cover: 'town' }, // the south pass fort
    { x: 700, y: 2160, rw: 50, rh: 40, cover: 'camp' }, // bandit camp
    { x: 1720, y: 2520, rw: 55, rh: 45, cover: 'ruin' }, // the ruined temple
    { x: 1080, y: 140, rw: 30, rh: 24, cover: 'ruin' }, // the headland fort
    { x: 2010, y: 190, rw: 35, rh: 25, cover: 'camp' }, // pirate cove
    { x: 2230, y: 980, rw: 26, rh: 22, cover: 'ruin' }, // bamboo temple
  ],
  // The stone forest: 28 spires scattered in this box north of the city, plus two sea stacks
  // (generated once, seeded).
  spires: {
    box: [880, 860, 400, 380],
    n: 28,
    seed: 9,
    floor: 42,
    avoidRoad: 'stoneforest',
    // The ten-thousand steps, Wudang's foot village and the boss's ground beside the box.
    avoid: [
      [850, 900, 70],
      [965, 830, 75],
      [1320, 1150, 55],
    ],
    extra: [
      { x: 1830, y: 70, r: 14, top: 62 },
      { x: 1870, y: 95, r: 10, top: 48 },
    ],
  },
  // Nothing rises above the golden summit.
  maxHeight: 380,
  stations: [
    { id: 'yanyu', name: 'หยานหยู', x: 1501, y: 477 },
    { id: 'waystation', name: 'ศาลาพักม้าสิบลี้', x: 1510, y: 935 },
    // The coach yard inside the north gate, east of ถนนหลวง.
    { id: 'hub', name: 'ลานศิลา', x: 1494, y: 1512 },
    { id: 'wudangfoot', name: 'เชิงเขายอดหมอก', x: 980, y: 840 },
    { id: 'lake', name: 'ทะเลสาบสราญ', x: 2140, y: 1580 },
    { id: 'southpass', name: 'ด่านใต้', x: 1490, y: 3290 },
  ],
  // Phase-1 warps at the future sites (OPEN_WORLD.md §2.3), and the ferry kept for M1.
  portals: [
    // Phase 1: the warp to today's hub map stands on the great plaza, south-west of the stone.
    { to: 'plains', side: 's', x: 1418.5, y: 1690.5, name: 'ลานศิลาเก้ากระบี่' },
    { to: 'mist-peak', side: 'w', x: 960.5, y: 820.5, name: 'หมู่บ้านเชิงเขายอดหมอก' },
    { to: 'dim-moon', side: 'e', x: 2150.5, y: 1560.5, name: 'เมืองริมทะเลสาบสราญ' },
    { to: 'bell-stone', side: 'w', x: 470.5, y: 2300.5, name: 'ด่านตะวันตก' },
    { to: 'pine-ink', side: 's', x: 1475.5, y: 3325.5, name: 'ด่านใต้' },
    { to: 'cinnabar', side: 's', x: 2560.5, y: 2925.5, name: 'ด่านผาแดง' },
    { to: 'lantern-road', side: 'n', x: 386.5, y: 620.5, name: 'ช่องเขาเหนือ' },
  ],
  // The open country's hunting grounds (places 8, 10, 18, 20), from the spawn tables of the zones
  // they take over (data/zones.ts: plains' wolves and bandits, mist-peak's graves and wilds). Modest
  // counts: each is set down when a player's active cells first touch it (systems/activity.ts).
  wilds: [
    {
      id: 'bamboo-sea',
      place: 8,
      name: 'ทะเลไผ่',
      rect: [1850, 2250, 720, 1080],
      spawns: [
        { monster: 'wolf', n: 8 },
        { monster: 'spider', n: 8 },
        { monster: 'python', n: 4 },
      ],
    },
    {
      id: 'wolf-fields',
      place: 20,
      name: 'ทุ่งหมาป่าเงาจันทร์',
      // Between the city and the bamboo sea (docs/archive/HUB.md §5).
      rect: [2020, 2220, 1080, 1280],
      spawns: [{ monster: 'wolf', n: 16 }],
    },
    {
      id: 'bandit-hills',
      place: 10,
      name: 'ค่ายโจรภูเขา',
      // Deeper in the hills, round the camp at (700, 2160).
      rect: [600, 800, 2060, 2260],
      spawns: [
        { monster: 'bandit', n: 14 },
        { monster: 'knifer', n: 4 },
      ],
    },
    {
      id: 'graves',
      place: 18,
      name: 'สุสานใต้เขา',
      rect: [690, 830, 1390, 1510],
      spawns: [{ monster: 'jiangshi', n: 10 }],
    },
  ],
};
